/**
 * VRG-575 — tag-shop customisation pricing. THE single source of truth.
 *
 * This value used to exist as four separate literals: the API's billing
 * constant, the platform cart's constant, and two hardcoded copies in display
 * components whose comments claimed to "mirror the API's". Two of the four
 * describing themselves as mirrors is precisely how they end up different, so
 * they are gone and every reader imports this.
 *
 * It lives in `@repo/types` because that package is already a real dependency of
 * BOTH `apps/api` (workspace dep, imported throughout) and `apps/platform`
 * (workspace dep + a vite alias, and already the source of value — not just
 * type — imports such as `ENCODABLE_TAG_FAMILIES`). So no package boundary
 * forces a second copy, and there is nothing for a drift guard to compare:
 * `customization-setup-fee.guard.spec.ts` instead asserts that no second
 * definition has reappeared.
 */

/**
 * What a company pays, ONCE PER ARTWORK, to originate a custom tag design.
 *
 * "Once per artwork" is the whole point of VRG-575 and is enforced server-side
 * by `company_customization_setups`: a (company, artwork) pair that already has
 * a setup recorded is never charged again, whatever it was charged the first
 * time. It is NOT once per line item and NOT once per order — both of which the
 * pre-VRG-575 code did, so three customised lines carrying one logo were billed
 * three times and every reorder of an approved design was billed again.
 *
 * $250, founder decision 2026-09-05, up from $100.
 *
 * ⚠️ This covers artwork ORIGINATION only. It deliberately does not attempt to
 * recover a per-unit premium on a custom production run: a one-time fee cannot
 * recover a recurring per-unit cost, which is arithmetic rather than a pricing
 * preference. That belongs in the unit price of custom tags and is out of scope
 * for VRG-575, blocked on the supplier's quote shape.
 */
export const CUSTOMIZATION_SETUP_FEE_USD = 250;
